import { useState, useRef, useEffect, useCallback } from 'react'
import { isAxiosError } from 'axios'
import { Badge } from '@/shared/ui/badge'
import { Checkbox } from '@/shared/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command'
import { Loader2, Plus, X } from 'lucide-react'
import apiClient from '@/shared/lib/apiClient'
import { instrumentTagsPath } from '@/shared/lib/routes'
import { t } from '@/shared/lib/i18n'

interface TagOption {
  value: number
  label: string
}

interface Props {
  selectedIds: number[]
  onChange: (ids: number[]) => void
  initialTags?: TagOption[]
  placeholder?: string
  className?: string
}

const SEARCH_DEBOUNCE_MS = 300

const InstrumentTagCombobox = ({
  selectedIds,
  onChange,
  initialTags = [],
  placeholder,
  className,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [fetchedOptions, setFetchedOptions] = useState<TagOption[]>([])
  const [knownOptions, setKnownOptions] = useState<TagOption[]>(initialTags)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const mergeIntoKnown = useCallback((incoming: TagOption[]) => {
    setKnownOptions((prev) =>
      [...prev, ...incoming].filter(
        (opt, index, self) => self.findIndex((o) => o.value === opt.value) === index,
      ),
    )
  }, [])

  useEffect(() => {
    mergeIntoKnown(initialTags)
  }, [initialTags, mergeIntoKnown])

  const fetchOptions = useCallback(
    (searchText: string) => {
      setLoading(true)
      void apiClient
        .get<TagOption[]>(instrumentTagsPath(), {
          params: searchText ? { search: searchText } : {},
        })
        .then(({ data }) => {
          setFetchedOptions(data)
          mergeIntoKnown(data)
        })
        .finally(() => setLoading(false))
    },
    [mergeIntoKnown],
  )

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setSearch('')
      setCreateError(null)
    }
  }

  useEffect(() => {
    if (!open) return

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    const delay = search === '' ? 0 : SEARCH_DEBOUNCE_MS
    debounceTimerRef.current = setTimeout(() => fetchOptions(search), delay)

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [open, search, fetchOptions])

  const toggle = (id: number) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((i) => i !== id) : [...selectedIds, id])
  }

  const handleCreate = async () => {
    const name = search.trim()
    if (!name || creating) return
    setCreating(true)
    setCreateError(null)
    try {
      const { data } = await apiClient.post<TagOption>(instrumentTagsPath(), {
        instrument_tag: { name },
      })
      const newOption: TagOption = { value: data.value, label: data.label }
      setFetchedOptions((prev) => [...prev, newOption])
      mergeIntoKnown([newOption])
      onChange([...selectedIds, data.value])
      setSearch('')
    } catch (error) {
      const message = isAxiosError(error)
        ? (error.response?.data?.error as string | undefined)
        : undefined
      setCreateError(message ?? t('instruments.form.createTagFailed'))
    } finally {
      setCreating(false)
    }
  }

  // Selected options float to the top; the backend already returns options
  // sorted by name, so a stable sort preserves alphabetical order per group.
  const sortedOptions = [...fetchedOptions].sort((a, b) => {
    const aSelected = selectedIds.includes(a.value)
    const bSelected = selectedIds.includes(b.value)
    if (aSelected && !bSelected) return -1
    if (!aSelected && bSelected) return 1
    return 0
  })

  const selectedTags = knownOptions.filter((opt) => selectedIds.includes(opt.value))

  return (
    <div className={className}>
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1">
          {selectedTags.map((tag) => (
            <Badge key={tag.value} variant="secondary" className="gap-1">
              {tag.label}
              <button
                type="button"
                onClick={() => toggle(tag.value)}
                className="ml-0.5 rounded-full"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setOpen(true)}
            className="flex h-9 w-full items-center rounded-md border border-input bg-background px-3 text-sm cursor-pointer ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <span className="text-muted-foreground">
              {placeholder ?? t('instruments.form.addTagPlaceholder')}
            </span>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t('instruments.form.searchTagsPlaceholder')}
              value={search}
              onValueChange={(value) => {
                setSearch(value)
                setCreateError(null)
              }}
            />
            <CommandList className="h-[200px]">
              {loading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
              {!loading && fetchedOptions.length === 0 && !search.trim() && (
                <CommandEmpty>{t('instruments.form.noTagsFound')}</CommandEmpty>
              )}
              {!loading && fetchedOptions.length > 0 && (
                <CommandGroup>
                  {sortedOptions.map((opt) => {
                    const isSelected = selectedIds.includes(opt.value)
                    return (
                      <CommandItem
                        key={opt.value}
                        onSelect={() => toggle(opt.value)}
                        className="gap-2"
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggle(opt.value)}
                          aria-hidden
                          tabIndex={-1}
                        />
                        <span className={isSelected ? 'font-medium' : ''}>{opt.label}</span>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )}
              {!loading && search.trim() && fetchedOptions.length === 0 && (
                <CommandGroup>
                  <CommandItem
                    onSelect={handleCreate}
                    disabled={creating}
                    className="gap-2 mx-1 my-1 rounded border border-dashed border-primary text-primary hover:bg-primary/10 aria-selected:bg-primary/10"
                  >
                    <Plus className="h-4 w-4 shrink-0" />
                    {creating
                      ? t('instruments.form.creatingTag')
                      : t('instruments.form.createTag', { name: search.trim() })}
                  </CommandItem>
                </CommandGroup>
              )}
              {createError && <p className="px-3 py-2 text-sm text-destructive">{createError}</p>}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default InstrumentTagCombobox
